(() => {
  if (window.DMProductImageZoom) return;
  window.DMProductImageZoom = true;

  const MIN_SCALE = 1;
  const MAX_SCALE = 4;
  const DOUBLE_TAP_SCALE = 2.5;
  const SWIPE_DISTANCE = 60;
  const DISMISS_DISTANCE = 100;
  const TRIGGER_DRAG_DISTANCE = 10;
  const ZOOM_HINT_STORAGE_KEY = 'dm:product-zoom-hint-seen';

  const clamp = (value, minimum, maximum) => Math.min(Math.max(value, minimum), maximum);
  const distance = (first, second) => Math.hypot(second.x - first.x, second.y - first.y);

  class ProductImageZoom {
    constructor(modal) {
      this.modal = modal;
      this.dialog = modal.querySelector('[role="dialog"]');
      this.viewport = modal.querySelector('[data-product-zoom-viewport]');
      this.image = modal.querySelector('[data-product-zoom-image]');
      this.counter = modal.querySelector('[data-product-zoom-counter]');
      this.hint = modal.querySelector('[data-product-zoom-hint]');
      this.previousButton = modal.querySelector('[data-product-zoom-previous]');
      this.nextButton = modal.querySelector('[data-product-zoom-next]');
      this.zoomInButton = modal.querySelector('[data-product-zoom-in]');
      this.zoomOutButton = modal.querySelector('[data-product-zoom-out]');
      this.resetButton = modal.querySelector('[data-product-zoom-reset]');
      this.shareButton = modal.querySelector('[data-product-zoom-share]');
      this.status = modal.querySelector('[data-product-zoom-status]');
      this.sources = [];
      this.activeIndex = 0;
      this.scale = MIN_SCALE;
      this.x = 0;
      this.y = 0;
      this.pointers = new Map();
      this.gestureStart = null;
      this.panStart = null;
      this.pinchStart = null;
      this.gestureWasPinch = false;
      this.lastTap = null;
      this.opener = null;
      this.previousBodyOverflow = '';
      this.hintTimer = null;
      this.statusTimer = null;
      this.suppressViewportClickUntil = 0;

      this.bindModalEvents();
    }

    bindModalEvents() {
      this.modal.querySelectorAll('[data-product-zoom-close]').forEach((button) => {
        button.addEventListener('click', () => this.close());
      });
      this.previousButton.addEventListener('click', () => this.showPrevious());
      this.nextButton.addEventListener('click', () => this.showNext());
      this.zoomInButton.addEventListener('click', () => this.setScale(this.scale + 0.5));
      this.zoomOutButton.addEventListener('click', () => this.setScale(this.scale - 0.5));
      this.resetButton.addEventListener('click', () => this.resetTransform());
      this.shareButton?.addEventListener('click', () => this.shareProduct());
      this.viewport.addEventListener('wheel', (event) => this.onWheel(event), { passive: false });
      this.viewport.addEventListener('pointerdown', (event) => this.onPointerDown(event));
      this.viewport.addEventListener('pointermove', (event) => this.onPointerMove(event));
      this.viewport.addEventListener('pointerup', (event) => this.onPointerUp(event));
      this.viewport.addEventListener('pointercancel', (event) => this.onPointerUp(event));
      this.viewport.addEventListener('click', (event) => this.onViewportClick(event));
      document.addEventListener('keydown', (event) => {
        if (!this.modal.hidden) this.onKeyDown(event);
      });
      window.addEventListener('resize', () => {
        if (!this.modal.hidden) this.constrainPan();
      });
    }

    open(trigger) {
      const gallery = trigger.closest('media-gallery, [data-catalog-gallery]') || trigger.closest('section');
      const triggers = Array.from(gallery?.querySelectorAll('[data-product-zoom-trigger]') || [trigger]);

      this.sources = triggers
        .map((item) => ({
          src: item.dataset.productZoomSrc,
          alt: item.dataset.productZoomAlt || '',
          trigger: item,
        }))
        .filter((item) => item.src);

      this.activeIndex = Math.max(0, triggers.indexOf(trigger));
      this.opener = trigger;
      this.suppressViewportClickUntil = 0;
      this.previousBodyOverflow = document.body.style.overflow;
      this.modal.hidden = false;
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('product-image-zoom-open');
      document.documentElement.classList.add('product-image-zoom-open');
      document.body.style.overflow = 'hidden';
      this.loadActiveImage();
      this.showFirstUseHint();
      this.dialog.focus({ preventScroll: true });
    }

    close() {
      if (this.modal.hidden) return;
      this.modal.hidden = true;
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('product-image-zoom-open');
      document.documentElement.classList.remove('product-image-zoom-open');
      document.body.style.overflow = this.previousBodyOverflow;
      this.image.removeAttribute('src');
      this.sources = [];
      this.pointers.clear();
      window.clearTimeout(this.hintTimer);
      window.clearTimeout(this.statusTimer);
      if (this.hint) {
        this.hint.classList.remove('is-visible');
        this.hint.hidden = true;
      }
      if (this.status) this.status.textContent = '';
      this.resetTransform();
      this.opener?.focus({ preventScroll: true });
    }

    showFirstUseHint() {
      if (!this.hint) return;

      try {
        if (localStorage.getItem(ZOOM_HINT_STORAGE_KEY) === 'true') return;
        localStorage.setItem(ZOOM_HINT_STORAGE_KEY, 'true');
      } catch (_error) {
        if (window.dmProductZoomHintSeen) return;
        window.dmProductZoomHintSeen = true;
      }

      window.clearTimeout(this.hintTimer);
      this.hint.hidden = false;
      this.hint.classList.remove('is-visible');
      requestAnimationFrame(() => this.hint.classList.add('is-visible'));
      this.hintTimer = window.setTimeout(() => {
        this.hint.classList.remove('is-visible');
        this.hint.hidden = true;
      }, 2100);
    }

    showStatus(message) {
      if (!this.status) return;
      window.clearTimeout(this.statusTimer);
      this.status.textContent = message;
      this.statusTimer = window.setTimeout(() => {
        this.status.textContent = '';
      }, 2200);
    }

    async shareProduct() {
      const title = document.querySelector('h1')?.textContent?.trim() || document.title;
      const url = window.location.href;
      const shareData = {
        title,
        text: `Mira este mueble de Diseños en Madera: ${title}`,
        url,
      };

      try {
        if (navigator.share) {
          await navigator.share(shareData);
          return;
        }

        if (navigator.clipboard?.writeText && window.isSecureContext) {
          await navigator.clipboard.writeText(url);
          this.showStatus('Enlace copiado');
          return;
        }

        this.showStatus('No se pudo compartir');
      } catch (error) {
        if (error.name !== 'AbortError') this.showStatus('No se pudo compartir');
      }
    }

    loadActiveImage() {
      const source = this.sources[this.activeIndex];
      if (!source) return;
      this.resetTransform();
      this.image.src = source.src;
      this.image.alt = source.alt;
      this.counter.textContent = `${this.activeIndex + 1} / ${this.sources.length}`;
      const hasMultipleImages = this.sources.length > 1;
      this.previousButton.hidden = !hasMultipleImages;
      this.nextButton.hidden = !hasMultipleImages;
    }

    showPrevious() {
      if (this.sources.length < 2) return;
      this.activeIndex = (this.activeIndex - 1 + this.sources.length) % this.sources.length;
      this.loadActiveImage();
    }

    showNext() {
      if (this.sources.length < 2) return;
      this.activeIndex = (this.activeIndex + 1) % this.sources.length;
      this.loadActiveImage();
    }

    setScale(nextScale, center = null) {
      const previousScale = this.scale;
      this.scale = clamp(nextScale, MIN_SCALE, MAX_SCALE);

      if (center && previousScale !== this.scale) {
        const rect = this.viewport.getBoundingClientRect();
        const centerX = center.x - rect.left - rect.width / 2;
        const centerY = center.y - rect.top - rect.height / 2;
        const ratio = this.scale / previousScale;
        this.x = centerX - (centerX - this.x) * ratio;
        this.y = centerY - (centerY - this.y) * ratio;
      }

      if (this.scale === MIN_SCALE) {
        this.x = 0;
        this.y = 0;
      }

      this.constrainPan();
    }

    resetTransform() {
      this.scale = MIN_SCALE;
      this.x = 0;
      this.y = 0;
      this.applyTransform();
    }

    constrainPan() {
      const maxX = Math.max(0, (this.image.clientWidth * (this.scale - 1)) / 2);
      const maxY = Math.max(0, (this.image.clientHeight * (this.scale - 1)) / 2);
      this.x = clamp(this.x, -maxX, maxX);
      this.y = clamp(this.y, -maxY, maxY);
      this.applyTransform();
    }

    applyTransform() {
      this.image.style.transform = `translate3d(${this.x}px, ${this.y}px, 0) scale(${this.scale})`;
      this.viewport.classList.toggle('is-zoomed', this.scale > MIN_SCALE);
      this.zoomOutButton.disabled = this.scale <= MIN_SCALE;
      this.zoomInButton.disabled = this.scale >= MAX_SCALE;
      this.resetButton.disabled = this.scale === MIN_SCALE && this.x === 0 && this.y === 0;
    }

    onWheel(event) {
      event.preventDefault();
      const direction = event.deltaY < 0 ? 0.25 : -0.25;
      this.setScale(this.scale + direction, { x: event.clientX, y: event.clientY });
    }

    onPointerDown(event) {
      this.viewport.setPointerCapture?.(event.pointerId);
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

      if (this.pointers.size === 1) {
        this.gestureStart = { x: event.clientX, y: event.clientY, time: Date.now() };
        this.panStart = { x: this.x, y: this.y, pointerX: event.clientX, pointerY: event.clientY };
        this.gestureWasPinch = false;
        this.viewport.classList.add('is-dragging');
      } else if (this.pointers.size === 2) {
        const points = Array.from(this.pointers.values());
        this.gestureWasPinch = true;
        this.pinchStart = {
          distance: distance(points[0], points[1]),
          scale: this.scale,
        };
      }
    }

    onPointerMove(event) {
      if (!this.pointers.has(event.pointerId)) return;
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

      if (this.pointers.size === 2 && this.pinchStart) {
        const points = Array.from(this.pointers.values());
        const currentDistance = distance(points[0], points[1]);
        const center = {
          x: (points[0].x + points[1].x) / 2,
          y: (points[0].y + points[1].y) / 2,
        };
        this.setScale(this.pinchStart.scale * (currentDistance / this.pinchStart.distance), center);
        return;
      }

      if (this.pointers.size === 1 && this.scale > MIN_SCALE && this.panStart) {
        this.x = this.panStart.x + event.clientX - this.panStart.pointerX;
        this.y = this.panStart.y + event.clientY - this.panStart.pointerY;
        this.constrainPan();
      }
    }

    onPointerUp(event) {
      const point = this.pointers.get(event.pointerId);
      this.pointers.delete(event.pointerId);
      this.viewport.classList.remove('is-dragging');

      if (!point || !this.gestureStart) return;

      const deltaX = event.clientX - this.gestureStart.x;
      const deltaY = event.clientY - this.gestureStart.y;
      const moved = Math.hypot(deltaX, deltaY);
      if (this.gestureWasPinch || moved >= 12) {
        this.suppressViewportClickUntil = performance.now() + 350;
      }

      if (!this.gestureWasPinch && this.scale === MIN_SCALE) {
        if (Math.abs(deltaX) >= SWIPE_DISTANCE && Math.abs(deltaX) > Math.abs(deltaY) * 1.2) {
          deltaX < 0 ? this.showNext() : this.showPrevious();
        } else if (deltaY >= DISMISS_DISTANCE && Math.abs(deltaX) < SWIPE_DISTANCE) {
          this.close();
          return;
        } else if (moved < 12) {
          const now = Date.now();
          if (this.lastTap && now - this.lastTap.time < 320 && distance(this.lastTap, point) < 32) {
            this.setScale(DOUBLE_TAP_SCALE, { x: event.clientX, y: event.clientY });
            this.lastTap = null;
          } else {
            this.lastTap = { x: event.clientX, y: event.clientY, time: now };
          }
        }
      } else if (!this.gestureWasPinch && this.scale > MIN_SCALE && moved < 12) {
        const now = Date.now();
        if (this.lastTap && now - this.lastTap.time < 320 && distance(this.lastTap, point) < 32) {
          this.resetTransform();
          this.lastTap = null;
        } else {
          this.lastTap = { x: event.clientX, y: event.clientY, time: now };
        }
      }

      if (this.pointers.size === 0) {
        this.gestureStart = null;
        this.panStart = null;
        this.pinchStart = null;
        this.gestureWasPinch = false;
      }
    }

    onViewportClick(event) {
      if (performance.now() < this.suppressViewportClickUntil || this.scale > MIN_SCALE) return;
      const imageRect = this.image.getBoundingClientRect();
      const isOutsideImage =
        event.clientX < imageRect.left ||
        event.clientX > imageRect.right ||
        event.clientY < imageRect.top ||
        event.clientY > imageRect.bottom;

      if (isOutsideImage) this.close();
    }

    onKeyDown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.showPrevious();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.showNext();
      } else if (event.key === '+' || event.key === '=') {
        event.preventDefault();
        this.setScale(this.scale + 0.5);
      } else if (event.key === '-') {
        event.preventDefault();
        this.setScale(this.scale - 0.5);
      } else if (event.key === '0') {
        event.preventDefault();
        this.resetTransform();
      } else if (event.key === 'Tab') {
        this.trapFocus(event);
      }
    }

    trapFocus(event) {
      const focusable = Array.from(
        this.dialog.querySelectorAll('button:not([hidden]):not([disabled]), [href], [tabindex]:not([tabindex="-1"])')
      ).filter((element) => element.offsetParent !== null);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  const initialize = () => {
    const modals = Array.from(document.querySelectorAll('[data-product-image-zoom]'));

    modals.forEach((modal) => {
      if (modal.dataset.productZoomReady === 'true') return;
      modal.dataset.productZoomReady = 'true';
      const zoom = new ProductImageZoom(modal);
      const section = modal.closest('section') || document;

      section.querySelectorAll('[data-product-zoom-trigger]').forEach((trigger) => {
        let startPoint = null;
        let suppressClickUntil = 0;

        trigger.addEventListener('pointerdown', (event) => {
          startPoint = { x: event.clientX, y: event.clientY };
        });

        trigger.addEventListener('pointermove', (event) => {
          if (!startPoint) return;
          if (distance(startPoint, { x: event.clientX, y: event.clientY }) > TRIGGER_DRAG_DISTANCE) {
            suppressClickUntil = performance.now() + 500;
          }
        });

        trigger.addEventListener('pointerup', () => {
          window.setTimeout(() => {
            startPoint = null;
          }, 0);
        });

        trigger.addEventListener('pointercancel', () => {
          startPoint = null;
        });

        trigger.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopImmediatePropagation();
          if (performance.now() < suppressClickUntil) return;
          zoom.open(trigger);
        }, { capture: true });

        if (trigger.tagName !== 'BUTTON' && trigger.tagName !== 'A') {
          trigger.addEventListener('keydown', (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            event.stopPropagation();
            zoom.open(trigger);
          });
        }
      });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', initialize);
})();
